import { useMsal } from '@azure/msal-react';
import Row from 'react-bootstrap/Row';
import {
    Alert,
} from 'react-bootstrap';
import { useEffect, useState } from 'react';
import {
    AcceptQuoteClient,
    AcceptQuotePreInfoDto,
    AccountsClient,
    AddressDetailsDto,
    DeliveryAndReturnStep,
} from '../../api/web-api-client';
import { prefixedPropertyOf } from '../../utils';
import { DeliveryAndReturnProps } from './types';
import TextAreaInput from '../../components/Inputs/TextAreaInput';
import TextInput from '../../components/Inputs/TextInput';
import RadioButtonGroup from '../../components/Inputs/RadioButtonGroup';
import HidableField from '../../components/forms/HidableField';
import ContactDetailsInput from '../../components/forms/CommonForms/ContactDetails';
import AddressLookup from '../../components/Inputs/AddressLookup';
import NumberInput from '../../components/Inputs/NumberInput';
import BlockUISpinner from '../../components/BlockUISpinner';
import { tokenRequest } from '../../authentication/authConfig';
import useAccountContext from '../../authentication/hooks';

import MailingLabel from '../../components/Utilities/mailingLabel';
import AppLogger from '../../instrumentation/AppLogger';
import DeliveryInstructions from '../../components/Utilities/deliveryInstructions';

const getName = prefixedPropertyOf<DeliveryAndReturnStep>('deliveryAndReturn');

function getNameForUse2(name: string | keyof DeliveryAndReturnStep, isSummary: boolean | undefined) {
    const fullname = isSummary ? getName(name as keyof DeliveryAndReturnStep) : name;
    return fullname;
}

const DeliveryAndReturn = (props: DeliveryAndReturnProps) => {
    const { isSummary, id } = props;
    const { accounts, instance } = useMsal();
    const [isLoading, setIsLoading] = useState(false);
    const [acceptQuotePreInfo, setAcceptQuotePreInfo] = useState<AcceptQuotePreInfoDto | undefined>();
    const [postalAddress, setPostalAddress] = useState<AddressDetailsDto | undefined>();
    const [streetAddress, setStreetAddress] = useState<AddressDetailsDto | undefined>();
    const account = useAccountContext();

    function getNameForUse(name: string | keyof DeliveryAndReturnStep): string { return getNameForUse2(name, isSummary); }

    const getFormattedAddress = (address: AddressDetailsDto | undefined) => {
        let formattedAddress = 'No Address Found';

        if (address !== undefined && address !== null && address.line1 !== null && address.suburb !== null && address.state !== null && address.postcode !== null) {
            if (address.suburb !== undefined && address.suburb !== null) formattedAddress = `${address.line1}, ${address.suburb!} ${address.state!} ${address.postcode!}`;
        }

        return formattedAddress;
    };

    useEffect(() => {
        const getAcceptQuotePreInfo = async () => {
            try {
                AppLogger.verbose('DeliveryAndReturn.getAcceptQuotePreInfo.', account?.details);
                const acceptQuoteClient = new AcceptQuoteClient();
                const accountsClient = new AccountsClient();
                const tokenResult = await instance.acquireTokenSilent({
                    ...tokenRequest,
                    account: accounts[0],
                });
                acceptQuoteClient.setAuthToken(tokenResult.accessToken);
                accountsClient.setAuthToken(tokenResult.accessToken);

                let defaultOrganisationId = -1;

                if (account !== null && account.details !== null && account.details.defaultOrganisationId !== null) {
                    defaultOrganisationId = account.details.defaultOrganisationId!;
                }
                const deliveryAndReturn = await acceptQuoteClient.getDeliveryAndReturn(id!);
                setAcceptQuotePreInfo(deliveryAndReturn.acceptQuotePreInfo);

                setStreetAddress(deliveryAndReturn.rfqOrganisation?.streetAddress);

                if (deliveryAndReturn.rfqOrganisation?.postalAddressSameAsStreetAddress) setPostalAddress(deliveryAndReturn.rfqOrganisation?.streetAddress);
                else setPostalAddress(deliveryAndReturn.rfqOrganisation?.postalAddress);
            } catch (error) {
                AppLogger.error('Failed to load AcceptQuotePreInfo', error as Error, { Id: id });
            }
        };

        const loadDataForDisplay = async () => {
            setIsLoading(true);
            await getAcceptQuotePreInfo();
            setIsLoading(false);
        };
        loadDataForDisplay();
    }, []);

    return (
        <>
            {isLoading && (
                <BlockUISpinner>
                    <p>Loading...</p>
                </BlockUISpinner>
            )}
            {!isSummary
                ? (
                    <Alert
                        variant='info'
                        data-testid='info-summary'
                        role='status'
                        aria-live='polite'
                        className='d-flex mb-4'
                    >
                        <div className='d-flex justify-content-center justify-content-md-start mb-3 mb-md-0'>
                            <div className='bgCircle mb-3 me-3'>
                                <i className='icon-info' aria-hidden='true' />
                            </div>
                        </div>
                        <div>
                            <strong>Important information</strong>
                            <p>
                                Delays in shipping instruments/artefacts to NMI may impact our service time frames. Please allow extra time for shipping to us.
                            </p>
                            <p>
                                Instruments/artefacts not clearly labelled with the Quotation ID may experience additional delays for calibration.
                            </p>
                            <p>
                                <strong>Note: </strong>
                                The client is responsible for all costs associated with delivery to NMI and all arrangements and costs associated with return.
                            </p>
                        </div>
                    </Alert>
                ) : null}
            <Row className='mb-4'>
                <h2 className={isSummary ? 'h3 my-3' : ''}>
                    Instrument/artefact delivery
                </h2>
                <h3 className={isSummary ? 'h4 my-3' : ''}>
                    Please deliver the instrument/artefact to NMI with the following label attached
                </h3>
                <MailingLabel {...acceptQuotePreInfo} />
                <DeliveryInstructions deliveryInstructions={acceptQuotePreInfo?.nmiFacilityDeliveryInstructions} />
            </Row>
            <Row className='mb-4'>
                <h2 className={isSummary ? 'h3 my-3' : ''}>
                    Instrument/artefact return
                </h2>
                <h3 className={isSummary ? 'h4 my-3' : ''}>
                    Return contact person
                </h3>
                <RadioButtonGroup
                    legend='Please select your contact person for the returned instrument/artefact when the job has completed'
                    name={getNameForUse('returnContactType')}
                    id='q-returnContactType'
                    isSummary={isSummary}
                    options={[
                        {
                            label: 'The main contact person for this request',
                            value: 'SamePerson',
                            id: 'returnContactType-SamePerson',
                        },
                        {
                            label: 'A different return contact person',
                            value: 'DifferentPerson',
                            id: 'returnContactType-DifferentPerson',
                        }]}
                />
            </Row>
            {isSummary && (
                <HidableField name={getNameForUse('rfqHide')}>
                    <Row className='mb-4'>
                        <h4 className={isSummary ? 'h5 my-3' : ''}>
                            Return contact person details
                        </h4>
                        <ContactDetailsInput
                            key='contact'
                            name='requestForQuote.contact'
                            isSummary={isSummary}
                        />
                    </Row>
                </HidableField>
            )}
            <HidableField name={getNameForUse('contactHide')}>
                <Row className='mb-4'>
                    <h4 className={isSummary ? 'h5 my-3' : ''}>
                        Return contact person details
                    </h4>
                    <ContactDetailsInput
                        key='contact'
                        name={getNameForUse('contact')}
                        isSummary={isSummary}
                    />
                </Row>
            </HidableField>
            <Row className='mb-4'>
                <h3 className={isSummary ? 'h4 my-3' : ''}>
                    Return method
                </h3>
                <RadioButtonGroup
                    legend='Choose your return method for the instrument/artefact'
                    name={getNameForUse('returnMethod')}
                    id='q-returnMethod'
                    isSummary={isSummary}
                    options={[
                        {
                            label: 'Client to arrange when completed',
                            value: 'ClientToArrange',
                            id: 'returnMethod-ClientToArrange',
                        },
                        {
                            label: 'Client will collect/pickup when completed',
                            value: 'ClientWillCollect',
                            id: 'returnMethod-ClientWillCollect',
                        },
                        {
                            label: 'Client will provide return address and carrier details below',
                            value: 'ClientWillProvide',
                            id: 'returnMethod-ClientWillProvide',
                        },
                    ]}
                />
                {!isSummary && (
                    <HidableField name='returnAddressType'>
                        <RadioButtonGroup
                            legend='Instrument/artefact return address'
                            name={getNameForUse('returnAddressType')}
                            id='q-returnAddressType'
                            inlineHelp={(<span>Please select your return address for the artefact/instrument</span>)}
                            isSummary={isSummary}
                            containerClassName='mb-3'
                            options={[
                                {
                                    label: 'Business address',
                                    value: 'BusinessAddress',
                                    descriptor: getFormattedAddress(streetAddress),
                                    id: 'returnAddressType-BusinessAddress',
                                },
                                {
                                    label: 'Postal address',
                                    value: 'PostalAddress',
                                    descriptor: getFormattedAddress(postalAddress),
                                    id: 'returnAddressType-PostalAddress',
                                },
                                {
                                    label: 'Other',
                                    value: 'Other',
                                    id: 'returnAddressType-Other',
                                },
                            ]}
                        />
                        {!isSummary && (
                            <HidableField name='returnAddress'>
                                <div className='px-5'>
                                    <AddressLookup
                                        name={getNameForUse('returnAddress')}
                                        label='Return address'
                                        maxResults={10}
                                        key='returnAddress'
                                        placeholder='Start typing and then select your address from the drop-down list'
                                    />
                                </div>
                            </HidableField>
                        )}
                        <TextAreaInput
                            label='Packaging notes (optional)'
                            name={getNameForUse('packagingNotes')}
                            inlineHelp={(
                                <>
                                    Please specify packaging requirements if different to that as received at this Laboratory, extra charges may apply.
                                    <span className='d-block pt-1'>
                                        Enter a maximum of 300 characters.
                                    </span>
                                </>
                            )}
                            rows={3}
                            maxCharacters={300}
                            isSummary={isSummary}
                        />
                        <h3 className={isSummary ? 'h4 my-3' : ''}>
                            Carrier
                        </h3>
                        <TextInput
                            label='Carrier name'
                            name={getNameForUse('carrierName')}
                            isSummary={isSummary}
                        />
                        <TextInput
                            label='Carrier account number or Pre-paid shipment reference'
                            name={getNameForUse('carrierAccountNumber')}
                            isSummary={isSummary}
                        />
                        <TextInput
                            label='Carrier contact person'
                            name={getNameForUse('carrierContactPerson')}
                            isSummary={isSummary}
                        />
                        <NumberInput
                            label='Carrier contact phone'
                            name={getNameForUse('carrierContactPhone')}
                            type='Phone'
                            isSummary={isSummary}
                            format='checkPhoneFormat'
                        />
                        <RadioButtonGroup
                            legend='Shipment mode'
                            name={getNameForUse('shipmentMode')}
                            id='q-shipmentMode'
                            isSummary={isSummary}
                            options={[
                                {
                                    label: 'Road',
                                    value: 'Road',
                                    id: 'shipmentMode-Road',
                                },
                                {
                                    label: 'Air',
                                    value: 'Air',
                                    id: 'shipmentMode-Air',
                                },

                            ]}
                        />
                        <RadioButtonGroup
                            legend='Shipment priority'
                            name={getNameForUse('shipmentPriority')}
                            id='q-shipmentPriority'
                            isSummary={isSummary}
                            options={[
                                {
                                    label: 'Standard',
                                    value: 'Standard',
                                    id: 'shipmentPriority-Standard',
                                },
                                {
                                    label: 'Same day',
                                    value: 'SameDay',
                                    id: 'shipmentPriority-SameDay',
                                },
                                {
                                    label: 'Next day',
                                    value: 'NextDay',
                                    id: 'shipmentPriority-NextDay',
                                },
                                {
                                    label: 'Off peak',
                                    value: 'OffPeak',
                                    id: 'shipmentPriority-OffPeak',
                                },
                                {
                                    label: 'Economy',
                                    value: 'Economy',
                                    id: 'shipmentPriority-Economy',
                                },
                                {
                                    label: 'Express',
                                    value: 'Express',
                                    id: 'shipmentPriority-Express',
                                },
                                {
                                    label: 'Overnight',
                                    value: 'Overnight',
                                    id: 'shipmentPriority-Overnight',
                                },
                                {
                                    label: 'Overnight first class priority',
                                    value: 'OvernightFirstClassPriority',
                                    id: 'shipmentPriority-OvernightFirstClassPriority',
                                },
                                {
                                    label: 'Priority',
                                    value: 'Priority',
                                    id: 'shipmentPriority-Priority',
                                },
                            ]}
                        />
                        <TextAreaInput
                            label='Insurance notes (optional)'
                            name={getNameForUse('insuranceNotes')}
                            inlineHelp={(
                                <>
                                    If the instrument/artefact is insured, are there any instructions that NMI must convey to your carrier?
                                    <span className='d-block pt-1'>
                                        Enter a maximum of 300 characters.
                                    </span>
                                </>
                            )}
                            rows={3}
                            maxCharacters={300}
                            isSummary={isSummary}
                        />
                        <NumberInput
                            label='For overseas client, Value for Australian Customs purposes (optional)'
                            name={getNameForUse('customsValue')}
                            isSummary={isSummary}
                            containerClassName='col-md-12 col-lg-10'
                            prepend='AUD$'
                            placeholder='0.00'
                            allowNegative={false}
                            thousandSeparator
                            fixedDecimalScale
                            decimalScale={2}
                            valueIsNumericString
                            maxLength={13} // Set to 13 rather than 10 to account for the thousands sep and decimal point
                        />
                        <TextAreaInput
                            label='Special instructions (optional)'
                            name={getNameForUse('specialInstructions')}
                            inlineHelp={(
                                <span className='d-block pt-1'>
                                    Enter a maximum of 300 characters.
                                </span>
                            )}
                            rows={3}
                            maxCharacters={300}
                            isSummary={isSummary}
                        />
                    </HidableField>
                )}
            </Row>
        </>
    );
};

export default DeliveryAndReturn;
